import styled from 'styled-components';
import React, {FC, useState, useCallback, ChangeEvent, useContext} from 'react';

import {GlobalStateContext} from '../../../common/layout';
import {mediaBreakpointDown, mediaBreakpointUp, mediaBreakpointBetween} from '../../../../lib/styleVariables';
import {SecondaryButton} from '../../../molecules/buttonsR4';
import {Input} from '../../../molecules/inputs';
import {NavigationCallToActionBase} from './navigationCallToActionBase';
import {NavigationCallToActionSectionTitle} from './navigationCallToActionSectionTitle';
import {
  fontSizesMixinsR4,
  fontSizesMixinsMobileR4,
  paletteR4,
  fontFamiliesR4
} from '../../../../lib/styleVariablesR4';

interface NavigationCallToActionWithEmailR4Props {
  title: React.ReactNode;
  submitUrl?: string;
  inputPlaceholder: string;
  buttonLabel: string;
  description: string;
}

const StyledNavigationCallToActionBase = styled(NavigationCallToActionBase)`
  flex-direction: column;
  background: transparent;
  padding: 42px 144px;
  ${mediaBreakpointDown('tablet')} {
    padding: 32px;
  }
  ${mediaBreakpointDown('mobile')} {
    padding: 16px 32px;
  }
`;

const StyledNavigationCallToActionSectionTitle = styled(NavigationCallToActionSectionTitle)`
  ${fontSizesMixinsR4.h1()};
  color: ${paletteR4.black.base};
  text-align: center;

  ${mediaBreakpointDown('tablet')} {
    ${fontSizesMixinsMobileR4.h1()};
  }
`;

const StyledDescriptionContainer = styled.div`
  font-family: ${fontFamiliesR4.body};
  font-size: 12px;
  line-height: 160%;
  text-align: center;
  margin: 42px 0 25px;
  ${mediaBreakpointDown('mobile')} {
    margin: 56px 0 16px;
  }
`;

const StyledForm = styled.div`
  display: flex;
  border: 2px solid ${paletteR4.black.base};
  height: 52px;
  background: ${paletteR4.white.base};
  margin: 0 auto;
  overflow: hidden;
  ${mediaBreakpointUp('tablet')} {
    max-width: 50%;
    border-radius: 100px;
    background: transparent;
  }
  ${mediaBreakpointUp('desktop')} {
    background: ${paletteR4.white.base};
  }
  ${mediaBreakpointBetween('tablet', 'desktop-wide')} {
    max-width: 70%;
    background: ${paletteR4.white.base};
  }
  ${mediaBreakpointDown('mobile')} {
    height: auto;
    flex-direction: column;
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
  }
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  border-radius: 8px 100px 100px 8px;
  border: none;
  margin-top: -2px;

  ${mediaBreakpointDown('mobile')} {
    margin-top: 16px;
    margin-left: 0;
    width: 100%;
    border-radius: 100px;
    height: 52px;
  }
`;

const inputWrapperStyle = `
  flex: 1;
`;

const StyledInput = styled(Input)`
  border: none;
  width: 100%;
  height: 100%;
  padding: 11px 11px 11px 24px;
  font-weight: 500;
  font-size: 16px;
  line-height: 160%;
  border-radius: 0;
  ${mediaBreakpointDown('mobile')} {
    width: 100%;
    border: 2px solid ${paletteR4.black.base};
    border-radius: 100px;
    padding: 11px 16px;
    height: 52px;
  }
`;

const contentContainerStyle = `
  width: 100%;
`;

const titleContainerStyle = `
  padding-right: 0px;
`;

export const NavigationCallToActionWithEmailR4: FC<NavigationCallToActionWithEmailR4Props> = props => {
  const {
    title,
    inputPlaceholder,
    buttonLabel,
    description
  } = props;
  const [email, setEmail] = useState<string>('');

  const onEmailChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value);
    },
    [email]
  );

  const globalState = useContext(GlobalStateContext);

  const onSubmit = () => {
    globalState.toggleGetStartedModal(true);
    globalState.setGetStartedModalEmail(email);
  };

  return (
    <StyledNavigationCallToActionBase
      renderLeft={() => (
        <StyledNavigationCallToActionSectionTitle>{title}</StyledNavigationCallToActionSectionTitle>
      )}
      rightCss={contentContainerStyle}
      leftCss={titleContainerStyle}
      renderRight={() => (
        <>
          <StyledDescriptionContainer>{description || ''}</StyledDescriptionContainer>
          <StyledForm>
            <StyledInput
              onDarkBackground
              placeholder={inputPlaceholder}
              type="email"
              name="email"
              onChange={onEmailChange}
              wrapperCss={inputWrapperStyle}
              onKeyDown={event => {
                if (event.key === 'Enter')
                  onSubmit();
              }}
            />
            <StyledSecondaryButton
              isHover
              type="button"
              componentType="button"
              onClick={onSubmit}
            >
              {buttonLabel}
            </StyledSecondaryButton>
          </StyledForm>
        </>
      )}
    />
  );
};
